import { Component, inject, OnInit, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import { Auth } from '@core/services';
import { Title } from '@shared/services';
import { Toaster } from '@shared/services';
import { Settings as SettingService } from '@modules/settings/services/settings';

@Component({
  selector: 'app-settings',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './settings.html',
  styleUrl: './settings.scss'
})
export class Settings implements OnInit {
  private fb = inject(FormBuilder);
  private authService = inject(Auth);
  private titleService = inject(Title);
  private toasterService = inject(Toaster);
  private settingService = inject(SettingService);

  passwordVisible = false;
  confirmPasswordVisible = false;

  user = computed(() => this.authService.user || {});
  userInitials = computed(() => {
    const f = this.user().firstName;
    const l = this.user().lastName;
    return f && l ? `${f[0]}${l[0]}`.toUpperCase() : '??';
  });

  profileForm: FormGroup = this.fb.group({
    firstName: [this.user().firstName, [Validators.required]],
    lastName: [this.user().lastName, [Validators.required]],
    email: [{ value: this.user().email, disabled: true }, [Validators.required, Validators.email]],
    countryCode: [this.user().country, [Validators.required]],
    phoneNumber: [this.user().mobile, [Validators.required]]
  });

  configForm: FormGroup = this.fb.group({
    senderIds: [{ value: this.user().senderIdNames || '', disabled: true }],
    package: [{ value: this.user().packageNames || '', disabled: true }]
  });

  passwordForm: FormGroup = this.fb.group(
    {
      new: ['', [Validators.required, Validators.minLength(6)]],
      confirm: ['', [Validators.required]]
    },
    {
      validators: this.passwordMatchValidator
    }
  );

  ngOnInit() {
    this.titleService.setTitle('Settings');
  }

  passwordMatchValidator(g: FormGroup) {
    return g.get('new')?.value === g.get('confirm')?.value ? null : { mismatch: true };
  }

  onUpdateProfile() {
    if (!this.profileForm.valid) {
      this.toasterService.errorAlert('Please fill in all required fields');
      return;
    }

    const user = {
      firstName: this.profileForm.value.firstName,
      lastName: this.profileForm.value.lastName,
      country: this.profileForm.value.countryCode,
      mobile: this.profileForm.value.phoneNumber
    };

    this.settingService.updateUser(this.user()._id.toString(), user).subscribe({
      next: (response: { data: any }) => {
        this.authService.user = response.data;
        this.toasterService.successAlert('Profile updated successfully');
      },
      error: () => {
        this.toasterService.errorAlert('Failed to update profile');
      }
    });
  }

  togglePassword() {
    this.passwordVisible = !this.passwordVisible;
  }

  toggleConfirmPassword() {
    this.confirmPasswordVisible = !this.confirmPasswordVisible;
  }

  onChangePassword() {
    if (!this.passwordForm.valid) {
      if (this.passwordForm.hasError('mismatch')) {
        this.toasterService.errorAlert('Passwords do not match');
      } else {
        this.toasterService.errorAlert('Please fill in all required fields');
      }
      return;
    }

    const user = {
      password: this.passwordForm.value.confirm
    };

    this.settingService.updateUser(this.user()._id.toString(), user).subscribe({
      next: () => {
        this.toasterService.successAlert('Password updated successfully');
      },
      error: () => {
        this.toasterService.errorAlert('Failed to update password');
      }
    });
  }
}
